import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {isEqual} from 'lodash';
import config from '../config.js';
import UserContext from '../components/common/UserContext.js';

// reactstrap
import {Col, Row} from 'reactstrap';

// kendo react
import {Label} from '@progress/kendo-react-labels';
import {DropDownList, MultiSelect} from '@progress/kendo-react-dropdowns';
import {InputPrefix} from '@progress/kendo-react-inputs';
import {Button} from '@progress/kendo-react-buttons';
import {Tooltip} from '@progress/kendo-react-tooltip';
import {userIcon} from '@progress/kendo-svg-icons';

// components
import PageHeader from '../components/common/PageHeader.js';
import Spinner from '../components/common/Spinner.js';
import Alert from '../components/common/Alert.js';
import EADashboard from '../components/EADashboards/EADashboard/EADashboard.js';
import FSADashboard from '../components/EADashboards/FSADashboard/FSADashboard.js';
import {capitalizeSentence} from '../components/common/utilities.js';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    allUsersKey,
    allUsersSelectedKey,
    userSelectedKey,
    usersSelectedKey,
    eADashboardKey,
    eANameKey,
    filtersKey,
    mainMessages,
    selectEAMessageKey,
    selectEATitleKey,
    userKey,
    yearKey
} from '../assets/text/MultilingualText.js';
import {filterBy} from "@progress/kendo-data-query";

// ea consts
const initialEA = {
    name: {type: ''},
    user: [],
    period: {},
    year: null
};
function EADashboards(props) {
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const localization = useLocalization();

    const [isLoading, setIsLoading] = useState(false);

    const [eA, setEA] = useState(initialEA);
    const [eANames, setEANames] = useState([]);
    const [users, setUsers] = useState([]);
    const [years, setYears] = useState([]);

    // users filter
    const [filter, setFilter] = useState([]);
    const ALL_USERS_KEY = localization.toLanguageString(allUsersKey, mainMessages[siteLanguageDefault][allUsersKey]);
    const INFO_ALERT = {
        type: 'info',
        title: localization.toLanguageString(selectEATitleKey, mainMessages[siteLanguageDefault][selectEATitleKey]),
        message: localization.toLanguageString(selectEAMessageKey, mainMessages[siteLanguageDefault][selectEAMessageKey])
    };

    const [alerts, setAlerts] = useState([INFO_ALERT]);

    /*
     * showHandler() handler for all alerts
    */
    const showHandler = (index) => setAlerts(alerts.filter((a, i) => i !== index));

    // ea name render
    const eANameRender = (li, props) => {
        let name = props.dataItem;
        const itemChildren = <span>
            <Row>
                <Col className={"ea-dashboard-no-wrap pe-0"}><b>{name.customer_name}</b></Col>
                {name?.remix_type && <Col className={"ea-dashboard-no-wrap ps-1"}>| {capitalizeSentence(name.remix_type)}</Col>}
            </Row>
            <Row className={"ps-1"}>
                <Col className={"ea-dashboard-no-wrap pe-0"}>{moment(name.start_date).format('ll')} to {moment(name.end_date).format('ll')}</Col>
                <Col className={"ea-dashboard-no-wrap ps-1"}>| {capitalizeSentence(name.status)}</Col>
            </Row>
        </span>;
        return React.cloneElement(li, li.props, itemChildren);
    };

    // users tooltip
    const UsersTooltip = () => <>
        {eA.user.filter(user => user !== ALL_USERS_KEY).map(user => <>{user}<br/></>)}
    </>;

    // users prefix
    const UsersPrefix = () => <InputPrefix orientation="vertical">
        <Button
            fillMode={'flat'}
            svgIcon={userIcon}
        />
    </InputPrefix>

    // users multiselect
    const UsersMultiSelect = () => <MultiSelect
        className={'ksm-dropdownlist'}
        value={eA.user}
        data={users}
        field="user"
        filterable={true}
        prefix={UsersPrefix}
        tags={eA.user.includes(ALL_USERS_KEY) ? [{
            text: localization.toLanguageString(allUsersSelectedKey, mainMessages[siteLanguageDefault][allUsersSelectedKey]),
            data: [...eA.user]
        }] : [{
            text: eA.user.length === 1 ? '1 ' + localization.toLanguageString(userSelectedKey, mainMessages[siteLanguageDefault][userSelectedKey])
                : eA.user.length + ' ' + localization.toLanguageString(usersSelectedKey, mainMessages[siteLanguageDefault][usersSelectedKey]),
            data: [...eA.user]
        }]}
        onChange={onEAChange}
        onFilterChange={onFilterChange}
    />

    /*
     * onFilterChange() filters the users search
     * @param {e} the input change event
    */
    const onFilterChange = (e) => {
        setUsers(filterBy(filter.slice(), e.filter));
    };

    /*
     * onEAChange() updates the EA object on change
     * @param {e} the input change event
    */
    const onEAChange = (e) => {
        let field = e.target.name || e.target.props.field;
        // handle select all users
        if (field === 'user') {
            let value = e.value;
            let difference = eA.user.filter(u => !e.value.includes(u));
            let compliment = e.value.filter(u => !eA.user.includes(u));

            // case: deselect all
            if (difference.includes(ALL_USERS_KEY)) {
                value = [];
            }
            // case: select all
            else if (compliment.includes(ALL_USERS_KEY)) {
                value = users;
            }
            // case: deselect a single user
            else if (difference.length > compliment.length) {
                value = value.filter(u => u !== ALL_USERS_KEY);
            }
            // case: select all users individually
            else if (isEqual(
                [...eA.user, ...compliment],
                [...value]
            ) && value.length === users.length - 1) {
                value = users;
            }

            setEA(eA => ({
                ...eA,
                [field]: value
            }));
        } else {
            // todo: next year chart
            setEA(eA => ({
                ...eA,
                [field]: e.value
            }));
        }
    }

    /*
     * getEA() gets all EA dropdown options for the ea name, user, and period dropdowns
    */
    function getEA() {
        let headers = {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + accessToken
        };

        setIsLoading(true);

        axios.get(
            config.ea_dashboard.EA,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    let data = response.data || [];
                    let names = data.map(d => {
                        let agreementType = d.agreement_type === "FSA" ? "FSA" : "EA";
                        let customerName = d.agreement_type === "FSA" ? "FSA - " + d.agreement : "EA - " + d.customer_name;
                        return {
                            id: d.agreement_type === "FSA" ? d.fsa_id : d.ea_id,
                            type: agreementType,
                            agreement: d.agreement,
                            customer_name: customerName,
                            remix_type: d.remix_type,
                            start_date: d.start_date,
                            end_date: d.end_date,
                            status: d.status,
                            name: d.agreement_type === "FSA" ? d.agreement
                                + " | " + moment(d.start_date).format('ll') + " to " + moment(d.end_date).format('ll')
                                + " | " + capitalizeSentence(d.status)
                                : d.customer_name
                                + " | " + capitalizeSentence(d.remix_type)
                                + " | " + moment(d.start_date).format('ll') + " to " + moment(d.end_date).format('ll')
                                + " | " + capitalizeSentence(d.status)
                        }
                    });
                    setEANames(names);
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to GET EA", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // 1. on load - initial page load
    useEffect(() => {
        getEA();
    }, []);

    // useEffect(() => {
    //     console.log("eA", eA)
    // }, [eA]);

    return (
        <>
            <PageHeader/>
            {isLoading ? <Spinner/> : <></>}
            <div className={"ksm-page-container"}>
                <div className={"k-h4"}>
                    {localization.toLanguageString(eADashboardKey, mainMessages[siteLanguageDefault][eADashboardKey])}
                </div>
                {!!alerts.length && <Row className={"mt-4"}>
                    <Col>
                        {alerts.map((alert, index) => <>
                            {alert.type === 'error' ? <Alert
                                className={"my-3 p-0"}
                                key={'error-' + index}
                                type={'error'}
                                title={alert.title}
                                message={alert.message}
                                showHandler={() => showHandler(index)}
                            /> : alert.type === 'success' ? <Alert
                                className={"my-3 p-0"}
                                key={'success-' + index}
                                type={'success'}
                                title={alert.title}
                                message={alert.message}
                                showHandler={() => showHandler(index)}
                            /> : alert.type === 'info' ? <Alert
                                className={"my-3 p-0"}
                                key={'info-' + index}
                                type={'info'}
                                title={alert.title}
                                message={alert.message}
                                showHandler={() => showHandler(index)}
                            /> : <></>}
                        </>)}
                    </Col>
                </Row>}
                <Row className={"mt-4 mb-2"}>
                    <Col className={"d-flex align-items-center"}>
                        <div className={"k-h5 mb-0"}>
                            {localization.toLanguageString(filtersKey, mainMessages[siteLanguageDefault][filtersKey])}
                        </div>
                    </Col>
                </Row>
                <Row className={"my-2"}>
                    <Col
                        xs={4}
                        className={"pe-1"}
                    >
                        <Label>
                            {localization.toLanguageString(eANameKey, mainMessages[siteLanguageDefault][eANameKey])}
                        </Label>
                        <Tooltip
                            anchorElement="target"
                            showCallout={true}
                            parentTitle={true}
                            openDelay={0}
                            position="top"
                        >
                            <DropDownList
                                title={eA.name.name}
                                className={'ksm-dropdownlist'}
                                value={eA.name}
                                data={eANames}
                                disabled={!eANames.length}
                                textField={'name'}
                                field="name"
                                itemRender={eANameRender}
                                onChange={onEAChange}
                            />
                        </Tooltip>
                    </Col>
                    <Col
                        xs={2}
                        className={"ps-1"}
                    >
                        <Label>
                            {localization.toLanguageString(userKey, mainMessages[siteLanguageDefault][userKey])}
                        </Label>
                        {eA.user.filter(u => u !== ALL_USERS_KEY).length ? <Tooltip
                            anchorElement="target"
                            showCallout={true}
                            parentTitle={true}
                            openDelay={0}
                            position="bottom"
                            content={UsersTooltip}
                        >
                        <span title={eA.user}>
                            <UsersMultiSelect/>
                        </span>
                        </Tooltip> : <UsersMultiSelect/>}
                    </Col>
                    <Col
                        xs={1}
                        className={"ps-1"}
                    >
                        <Label>
                            {localization.toLanguageString(yearKey, mainMessages[siteLanguageDefault][yearKey])}
                        </Label>
                        <DropDownList
                            value={eA.year}
                            data={years}
                            disabled={!years.length}
                            field="year"
                            onChange={onEAChange}
                        />
                    </Col>
                </Row>
                {eA.name.type === "EA" && <Row>
                    <Col>
                        <EADashboard
                            eA={eA}
                            setEA={setEA}
                            users={users}
                            setUsers={setUsers}
                            setYears={setYears}
                            setFilter={setFilter}
                            setAlerts={setAlerts}
                            setIsLoading={setIsLoading}
                            onEAChange={onEAChange}
                            INFO_ALERT={INFO_ALERT}
                            ALL_USERS_KEY={ALL_USERS_KEY}
                        />
                    </Col>
                </Row>}
                {eA.name.type === "FSA" && <Row>
                    <Col>
                        <FSADashboard
                            eA={eA}
                            setEA={setEA}
                            users={users}
                            setUsers={setUsers}
                            setYears={setYears}
                            setFilter={setFilter}
                            setAlerts={setAlerts}
                            setIsLoading={setIsLoading}
                            INFO_ALERT={INFO_ALERT}
                            ALL_USERS_KEY={ALL_USERS_KEY}
                        />
                    </Col>
                </Row>}
            </div>
        </>
    );
}

export default EADashboards;